import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {SaccLineConnUuid} from '@/subaccount/outstandingBalance/SaccLineConn'

type SaccLineConn = {
    debitLine: SubAccountLine
    creditLine: SubAccountLine
}

const canAddPair = (connectionUuids: SaccLineConnUuid[], debitLineUuid: string, creditLineUuid: string): Boolean => {
    return !connectionUuids.some(({debitLineUuid: debit, creditLineUuid: credit}) => debit === debitLineUuid && credit === creditLineUuid)
}

class SaccLineConnBuilder {
    private connections: SaccLineConn[] = []
    private remainderLines: [SubAccountLine, number][] = []
    private lines: SubAccountLine[] = []

    constructor() {
    }

    addAll(lines: SubAccountLine[]) {
        lines.forEach((line) => {
            if (!this.lines.some(l => l.uuid === line.uuid)) {
                this.add(line)
                this.lines.push(line)
            }
        })
        this.lines.forEach((line) => {
            if (!lines.some(l => l.uuid === line.uuid)) {
                this.remove(line)
            }
        })
    }

    isNotConnected(line: SubAccountLine) {
        return !this.connections
            .some(({debitLine, creditLine}) => debitLine.uuid === line.uuid || creditLine.uuid === line.uuid)
        && this.remainderLines.some(([remainderLine]) => remainderLine.uuid === line.uuid)
    }

    isValid() {
        return this.connections.length > 0 && this.lines.every(l => l.isOpen)
    }

    getConnections(): SaccLineConnUuid[] {
        return this.connections.map(({debitLine, creditLine}) => ({
            debitLineUuid: debitLine.uuid,
            creditLineUuid: creditLine.uuid
        }))
    }

    getRemainderLines() {
        return this.remainderLines
    }

    getRemainder() {
        return this.remainderLines.reduce((acc, remainder) => acc + remainder[1], 0)
    }

    add(line: SubAccountLine) {
        if (this.remainderLines.length === 0 || this.sameSign(this.remainderLines.slice(-1)![0][1], line.remainingAmount)) {
            this.remainderLines.push([line, line.remainingAmount])
        } else {
            this.connectLine([line, line.remainingAmount])
        }
    }

    clear() {
        this.remainderLines = []
        this.connections = []
        this.lines = []
    }

    remove(line: SubAccountLine) {
        this.remainderLines = []
        this.connections = []
        this.lines = this.lines
            .filter((l) => l.uuid !== line.uuid)
        this.lines
            .forEach((l) => this.add(l))
    }

    private sameSign(a: number, b: number): boolean {
        return (a * b) > 0
    }

    private connectLine(line: [SubAccountLine, number]) {
        const [remainderLine, remainder] = this.remainderLines.pop()!
        if (this.sameSign(remainder, line[1])) {
            throw new Error('Cannot connect lines with same sign')
        }
        this.addConnection(remainderLine, line[0])
        const newRemainder = remainder + line[1]

        if (newRemainder === 0) {
            return
        } else if (Math.abs(remainder) > Math.abs(line[1])) {
            this.remainderLines.push([remainderLine, newRemainder])
        } else {
            this.remainderLines.push([line[0], newRemainder])
            if (this.remainderLines.length > 1) {
                this.connectLine(this.remainderLines.pop()!)
            }
        }
    }

    private addConnection(line1: SubAccountLine, line2: SubAccountLine) {
        if (line1.amount > line2.amount) {
            this.connections.push({debitLine: line1, creditLine: line2})
        } else {
            this.connections.push({debitLine: line2, creditLine: line1})
        }
    }
}

export {SaccLineConnBuilder, canAddPair}